/**
 * Strict JSON Schema for Food Analysis
 * Enforces same-category alternatives and validates all responses
 */

import { z } from 'zod';

/**
 * Food category values - alternatives MUST stay in the same category
 */
const FOOD_CATEGORIES = [
  'Frozen dessert',
  'Dairy dessert',
  'Baked sweet',
  'Savory snack',
  'Fast food',
  'Beverage',
  'Meal',
  'Candy',
  'Fruit',
  'Vegetable',
  'Protein',
  'Other'
] as const;

export type FoodCategory = typeof FOOD_CATEGORIES[number];

/**
 * Food category schema - validates against enum values
 */
export const FoodCategorySchema = z.string().refine(
  (val) => FOOD_CATEGORIES.includes(val as FoodCategory),
  { message: 'Invalid food category' }
) as z.ZodType<FoodCategory>;

/**
 * Acne impact level values
 */
const ACNE_IMPACT_LEVELS = ['low', 'medium', 'high'] as const;

export type AcneImpact = typeof ACNE_IMPACT_LEVELS[number];

/**
 * Acne impact schema
 */
export const AcneImpactSchema = z.string().refine(
  (val) => ACNE_IMPACT_LEVELS.includes(val as AcneImpact),
  { message: 'Invalid acne impact level' }
) as z.ZodType<AcneImpact>;

/**
 * Recipe ingredient schema
 * Note: amount is stored as string/number in JSON, but we'll accept either
 */
export const RecipeIngredientSchema: z.ZodType<{
  amount: number | string;
  unit: string;
  name: string;
  notes?: string;
}> = z.object({
  amount: z.any(), // Accept any type (will be validated in mapping)
  unit: z.string(),
  name: z.string(),
  notes: z.string().optional()
}) as any;

/**
 * Recipe schema for alternatives
 */
export const RecipeSchema = z.object({
  title: z.string().min(1),
  ingredients: z.array(RecipeIngredientSchema).min(1),
  steps: z.array(z.string()).min(1),
  servings: z.number().int().positive(),
  caloriesPerServing: z.number().int().positive(),
  cuisine: z.string().optional(),
  notes: z.string().optional()
});

/**
 * Better alternative schema - must have sameCategory=true
 */
export const BetterAlternativeSchema = z.object({
  name: z.string().min(1),
  sameCategory: z.boolean().refine((val) => val === true, { message: 'sameCategory must be true' }),
  swapReason: z.string().min(1),
  recipe: RecipeSchema // Recipe is required for each alternative
});

/**
 * Brand schema for healthier snack brands
 */
export const BrandSchema = z.object({
  name: z.string().min(1),
  description: z.string().min(1),
  acneImpactScore: z.number().int().min(1).max(100),
  whyHealthy: z.string().min(1)
});

/**
 * Main food analysis response schema
 */
export const FoodAnalysisResponseSchema = z.object({
  food: z.string().min(1),
  category: FoodCategorySchema,
  acneImpact: AcneImpactSchema,
  why: z.array(z.string()).min(1),
  betterAlternatives: z.array(BetterAlternativeSchema).min(3).max(6), // 3-6 alternatives required, each with recipe
  brands: z.array(BrandSchema).min(2).max(5), // 2-5 brands required for healthier snack brands
  portionTips: z.array(z.string()).optional().default([]),
  warningIfAny: z.array(z.string()).optional().default([])
});

export type FoodAnalysisResponse = z.infer<typeof FoodAnalysisResponseSchema>;

/**
 * Validate and parse food analysis response
 * Returns validated data or throws ZodError with details
 */
export function validateFoodAnalysisResponse(data: unknown): FoodAnalysisResponse {
  return FoodAnalysisResponseSchema.parse(data);
}

/**
 * Safe validation with fallback - returns validated data or null
 */
export function safeValidateFoodAnalysisResponse(data: unknown): FoodAnalysisResponse | null {
  const result = FoodAnalysisResponseSchema.safeParse(data);
  if (result.success) {
    return result.data;
  }
  console.error('[FoodAnalysisSchema] Validation failed:', result.error.issues);
  return null;
}

/**
 * Create fallback response structure
 */
export function createFallbackResponse(foodName: string): FoodAnalysisResponse {
  return {
    food: foodName,
    category: 'Other',
    acneImpact: 'medium',
    why: [`Analysis for ${foodName}. Please try again for detailed information.`],
    betterAlternatives: [],
    brands: [],
    portionTips: [],
    warningIfAny: []
  };
}

